<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container">

      <!-- Header -->
      <div class="modal-header">
        <div class="header-left">
          <div class="header-icon">
            <i class="fas fa-lightbulb"></i>
          </div>
          <div>
            <h2 class="header-title">I Wonder If…</h2>
            <p class="header-sub">Just say it — Pollie figures out the rest</p>
          </div>
        </div>
        <button class="close-btn" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="modal-body">

        <!-- Success state -->
        <div v-if="submitted" class="success-state">
          <i class="fas fa-check-circle success-icon"></i>
          <h3>It's on the board!</h3>
          <p>Find it in the Dreams or Gremlins tab. Use the ··· menu to get Pollie's Take.</p>
          <button class="btn-primary" @click="$emit('close')">Back to Board</button>
        </div>

        <!-- Pipeline state -->
        <div v-else-if="submitting" class="pipeline-state">
          <div class="pipeline-title">
            <i class="fas fa-wand-magic-sparkles"></i> Pollie is on it…
          </div>
          <div
            v-for="step in pipelineSteps"
            :key="step.id"
            class="pipe-step"
            :class="{ show: step.visible }"
          >
            <div class="pipe-icon" :class="step.iconClass">
              <i :class="'fas ' + (step.done ? step.doneFa : step.fa)"></i>
            </div>
            <div class="pipe-body">
              <div class="pipe-label" :class="{ running: step.running, done: step.done }">
                {{ step.label }}
              </div>
              <div v-if="step.result" class="pipe-result" :class="step.resultCls">
                <i :class="'fas ' + step.resultIcon"></i> {{ step.result }}
              </div>
            </div>
            <div class="pipe-status">
              <i v-if="step.running" class="fas fa-spinner fa-spin pipe-spin"></i>
              <i v-else-if="step.done && step.warn" class="fas fa-check pipe-warn"></i>
              <i v-else-if="step.done" class="fas fa-check pipe-check"></i>
            </div>
          </div>
        </div>

        <!-- Form state -->
        <form v-else @submit.prevent="handleSubmit">
          <!-- Main input -->
          <div class="iwi-wrap">
            <span class="iwi-prefix">I wonder if…</span>
            <input
              ref="mainInput"
              v-model="ideaText"
              class="iwi-input"
              type="text"
              placeholder="a dream, a gremlin, a how-to?"
              autocomplete="off"
              :class="{ error: showError }"
            />
          </div>
          <p v-if="showError" class="field-error">Tell us what you're wondering first.</p>

          <!-- On behalf of — only shown when there's more than one org to choose from -->
          <div v-if="onBehalfOfOptions.length > 1" class="on-behalf-of">
            <label class="on-behalf-of-label">On behalf of</label>
            <select v-model="onBehalfOfOrgId" class="on-behalf-of-select">
              <option value="">Same as mine</option>
              <option v-for="org in onBehalfOfOptions" :key="org.id" :value="org.id">
                {{ org.display_name || org.name }}
              </option>
            </select>
          </div>

          <!-- More detail -->
          <button type="button" class="detail-toggle" :class="{ open: showDetail }" @click="showDetail = !showDetail">
            <i class="fas fa-chevron-down"></i>
            Add more detail (optional)
          </button>
          <textarea
            v-if="showDetail"
            v-model="detailText"
            class="detail-textarea"
            rows="3"
            placeholder="Any context, steps, or background…"
          ></textarea>

          <!-- Secure file upload -->
          <div class="upload-zone" @click="$refs.fileInput.click()">
            <i class="fas fa-shield-halved" style="color:#2db385"></i>
            <div>
              <strong>Secure Upload</strong>
              <span>Screenshots and files — stored in private, HIPAA-covered storage</span>
            </div>
          </div>
          <div v-if="attachedFiles.length" class="file-chip-list">
            <div v-for="(f, idx) in attachedFiles" :key="idx" class="file-chip">
              <i :class="f.type === 'application/pdf' ? 'fas fa-file-pdf' : 'fas fa-image'"></i>
              <span class="file-name">{{ f.name }}</span>
              <button type="button" class="remove-file" @click="removeFile(idx)">
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
          <input
            ref="fileInput"
            type="file"
            accept="image/*,.pdf"
            multiple
            style="display:none"
            @change="handleFileSelect"
          />

          <!-- AI hint -->
          <div class="ai-hint">
            <i class="fas fa-wand-magic-sparkles"></i>
            Pollie will classify this automatically — no need to categorize it yourself.
          </div>

          <!-- Submit error -->
          <div v-if="submitError" class="submit-error">
            <i class="fas fa-exclamation-triangle"></i> {{ submitError }}
          </div>

          <!-- Actions -->
          <div class="form-actions">
            <button type="button" class="btn-secondary" @click="$emit('close')">Cancel</button>
            <button type="submit" class="btn-primary">
              <i class="fas fa-plus"></i> Add It
            </button>
          </div>
        </form>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, nextTick, onMounted } from 'vue';
import observabilityAPI from '@/services/observabilityAPI';
import authService from '@/services/auth';

const emit = defineEmits(['close', 'submitted']);

// ── State ──────────────────────────────────────────────────────────
const ideaText       = ref('');
const detailText     = ref('');
const showDetail     = ref(false);
const attachedFiles  = ref([]);
const showError      = ref(false);
const submitError    = ref('');
const submitting     = ref(false);
const submitted      = ref(false);
const mainInput      = ref(null);

// ── On behalf of ───────────────────────────────────────────────────
// Which org this feedback is actually about, if different from whoever/whatever
// context is filing it (e.g. a multi-org user viewing Utah reporting for Colorado).
// Only shown when there's more than one org to choose from.
const onBehalfOfOrgId  = ref('');
const onBehalfOfOptions = ref([]);

async function loadOnBehalfOfOptions() {
  try {
    if (authService.hasPlatformAccess()) {
      onBehalfOfOptions.value = await observabilityAPI.getClientOrgCatalog();
    } else {
      const data = await observabilityAPI.getMySelectableOrganizations();
      onBehalfOfOptions.value = data.organizations || [];
    }
  } catch (e) {
    console.error('Failed to load on-behalf-of organizations:', e);
    onBehalfOfOptions.value = [];
  }
}

// ── Pipeline steps ─────────────────────────────────────────────────
const pipelineSteps = reactive([
  { id: 'read',  label: 'Reading your idea',                  fa: 'fa-file-lines',  doneFa: 'fa-file-lines',  visible: false, running: false, done: false, warn: false, iconClass: '', result: '', resultCls: '', resultIcon: '' },
  { id: 'type',  label: 'Figuring out what kind of thing this is', fa: 'fa-tag', doneFa: 'fa-tag', visible: false, running: false, done: false, warn: false, iconClass: '', result: '', resultCls: '', resultIcon: '' },
  { id: 'pri',   label: 'Setting ACCESS priority',            fa: 'fa-bolt',        doneFa: 'fa-bolt',        visible: false, running: false, done: false, warn: false, iconClass: '', result: '', resultCls: '', resultIcon: '' },
  { id: 'save',  label: 'Adding to the board',                fa: 'fa-plus',        doneFa: 'fa-check',       visible: false, running: false, done: false, warn: false, iconClass: '', result: '', resultCls: '', resultIcon: '' },
]);

function resetSteps() {
  pipelineSteps.forEach(s => {
    s.visible = false; s.running = false; s.done = false; s.warn = false;
    s.iconClass = ''; s.result = ''; s.resultCls = ''; s.resultIcon = '';
  });
}

function runStep(id, delay) {
  return new Promise(resolve => setTimeout(() => {
    const s = pipelineSteps.find(x => x.id === id);
    if (s) { s.visible = true; s.running = true; s.iconClass = 'running'; }
    resolve();
  }, delay));
}

function doneStep(id, { result, resultCls, resultIcon, warn, doneFa } = {}) {
  const s = pipelineSteps.find(x => x.id === id);
  if (!s) return;
  s.running = false; s.done = true; s.warn = !!warn;
  s.iconClass = warn ? 'done-warn' : 'done';
  if (doneFa) s.doneFa = doneFa;
  if (result) { s.result = result; s.resultCls = resultCls || ''; s.resultIcon = resultIcon || 'fa-tag'; }
}

// ── Client-side classification (instant, no AI needed for submit) ──
function classifyLocally(text) {
  const lower = text.toLowerCase();
  const isBug = /broken|error|bug|doesn't work|doesnt work|not working|fix|crash|fail|glitch/.test(lower);
  const isQ   = text.includes('?') || /^(why|how|what|when|where)\b/.test(lower);
  return {
    feedbackType: isBug ? 'bug'     : isQ ? 'question' : 'feature',
    typeLabel:    isBug ? 'Gremlin' : isQ ? 'Question'  : 'Idea',
    typeIcon:     isBug ? 'fa-bug'  : isQ ? 'fa-question-circle' : 'fa-lightbulb',
    typeCls:      isBug ? 'result-gremlin' : 'result-idea',
    priority:     isBug ? 'activate' : 'consider',
    priorityLabel: isBug ? 'Activate' : 'Consider',
    priorityIcon:  isBug ? 'fa-bolt'  : 'fa-eye',
    priorityCls:   isBug ? 'result-activate' : 'result-consider',
    warn: isBug,
  };
}

// ── Submit ─────────────────────────────────────────────────────────
async function handleSubmit() {
  showError.value = false;
  submitError.value = '';

  if (!ideaText.value.trim()) {
    showError.value = true;
    mainInput.value?.focus();
    return;
  }

  const raw  = ideaText.value.trim();
  const cls  = classifyLocally(raw);
  const full = detailText.value.trim() ? `${raw}\n\n${detailText.value.trim()}` : raw;

  // Title: first 150 chars, trimmed at word boundary
  let title = raw.length > 150 ? raw.slice(0, 150).replace(/\s+\S*$/, '…') : raw;

  submitting.value = true;
  resetSteps();

  // Animate pipeline
  await runStep('read', 80);
  await new Promise(r => setTimeout(r, 500));
  doneStep('read');

  await runStep('type', 0);
  await new Promise(r => setTimeout(r, 600));
  doneStep('type', { result: cls.typeLabel, resultCls: cls.typeCls, resultIcon: cls.typeIcon, warn: cls.warn });

  await runStep('pri', 0);
  await new Promise(r => setTimeout(r, 500));
  doneStep('pri', { result: cls.priorityLabel, resultCls: cls.priorityCls, resultIcon: cls.priorityIcon });

  await runStep('save', 0);

  try {
    const context = observabilityAPI.captureContext();
    const payload = {
      feedback_type: cls.feedbackType,
      title,
      description: full,
      priority: cls.priority,
      origin: 'Manual',
      ...context,
    };
    if (onBehalfOfOrgId.value) {
      payload.on_behalf_of_organization = onBehalfOfOrgId.value;
    }

    const created = await observabilityAPI.submitFeedback(payload);

    // Upload any attached files now that we have the card ID
    if (attachedFiles.value.length && created.id) {
      try {
        await Promise.all(
          attachedFiles.value.map(f => observabilityAPI.uploadAttachment(created.id, f))
        );
      } catch (uploadErr) {
        console.error('Attachment upload failed:', uploadErr);
        // Card was created — don't block success, but show a warning
        submitError.value = 'Card saved, but file upload failed. Try attaching files from the expanded card.';
      }
    }

    doneStep('save');

    await new Promise(r => setTimeout(r, 300));
    submitting.value = false;
    submitted.value  = true;
    emit('submitted', created);

  } catch (err) {
    submitting.value = false;
    submitError.value = err.response?.data?.detail
      || err.response?.data?.message
      || Object.values(err.response?.data || {})[0]?.[0]
      || 'Something went wrong. Please try again.';
    console.error('Submit failed:', err);
  }
}

// ── File select ────────────────────────────────────────────────────
function handleFileSelect(event) {
  const incoming = Array.from(event.target.files || []);
  attachedFiles.value = [...attachedFiles.value, ...incoming];
  // Reset so the same file can be re-added after removal
  event.target.value = '';
}

function removeFile(idx) {
  attachedFiles.value = attachedFiles.value.filter((_, i) => i !== idx);
}

// ── Auto-focus ─────────────────────────────────────────────────────
onMounted(() => {
  nextTick(() => mainInput.value?.focus());
  loadOnBehalfOfOptions();
});
</script>

<style scoped>
.modal-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 2000; padding: 20px;
}

.modal-container {
  background: white; border-radius: 16px;
  width: 100%; max-width: 480px;
  box-shadow: 0 24px 64px rgba(0,0,0,0.22);
  overflow: hidden;
}

/* ── Header ── */
.modal-header {
  display: flex; justify-content: space-between; align-items: flex-start;
  padding: 22px 24px 0;
}
.header-left { display: flex; align-items: center; gap: 12px; }
.header-icon {
  width: 42px; height: 42px; border-radius: 12px;
  background: linear-gradient(135deg, #2b7de9, #0d9488);
  display: flex; align-items: center; justify-content: center;
  font-size: 19px; color: white; flex-shrink: 0;
}
.header-title { font-size: 19px; font-weight: 700; color: #1a202c; margin: 0; }
.header-sub   { font-size: 12px; color: #a0aec0; margin: 2px 0 0; }
.close-btn {
  background: #f0f2f5; border: none; color: #718096;
  width: 28px; height: 28px; border-radius: 7px;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  font-size: 12px; flex-shrink: 0;
}
.close-btn:hover { background: #e2e8f0; }

/* ── Body ── */
.modal-body { padding: 20px 24px 24px; }

/* ── Main input ── */
.iwi-wrap { position: relative; margin-bottom: 10px; }
.iwi-prefix {
  position: absolute; left: 14px; top: 13px;
  font-size: 13px; font-weight: 600; color: #2b7de9; font-style: italic;
  pointer-events: none; white-space: nowrap;
}
.iwi-input {
  width: 100%; padding: 12px 14px 12px 116px;
  border: 2px solid #e2e8f0; border-radius: 10px;
  font-size: 13px; font-family: inherit; color: #1a202c;
  background: #f8fafc; transition: border-color 0.2s;
}
.iwi-input:focus { outline: none; border-color: #2b7de9; box-shadow: 0 0 0 3px rgba(43,125,233,0.1); background: white; }
.iwi-input.error { border-color: #c53030; }
.iwi-input::placeholder { color: #cbd5e0; }
.field-error { font-size: 12px; color: #c53030; margin: -4px 0 8px; }

/* ── On behalf of ── */
.on-behalf-of { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.on-behalf-of-label { font-size: 12px; color: #718096; font-weight: 600; white-space: nowrap; }
.on-behalf-of-select {
  flex: 1; padding: 7px 10px;
  border: 1px solid #e2e8f0; border-radius: 8px;
  font-size: 13px; font-family: inherit; color: #1a202c;
  background: #f8fafc;
}
.on-behalf-of-select:focus { outline: none; border-color: #2b7de9; background: white; }

/* ── Detail toggle ── */
.detail-toggle {
  background: none; border: none; cursor: pointer;
  font-size: 12px; color: #a0aec0; display: flex; align-items: center; gap: 5px;
  font-family: inherit; margin-bottom: 0; padding: 0;
}
.detail-toggle i { font-size: 10px; transition: transform 0.2s; }
.detail-toggle.open i { transform: rotate(180deg); }
.detail-toggle:hover { color: #2b7de9; }
.detail-textarea {
  width: 100%; margin-top: 8px; padding: 9px 12px;
  border: 1px solid #e2e8f0; border-radius: 8px;
  font-size: 13px; font-family: inherit; resize: none; min-height: 64px;
  background: #f8fafc;
}
.detail-textarea:focus { outline: none; border-color: #2b7de9; background: white; }

/* ── Upload ── */
.upload-zone {
  margin-top: 12px; border: 2px dashed #e2e8f0; border-radius: 10px;
  padding: 11px 14px; cursor: pointer;
  display: flex; align-items: center; gap: 10px;
  color: #a0aec0; font-size: 13px; transition: all 0.2s;
}
.upload-zone i { font-size: 17px; flex-shrink: 0; }
.upload-zone strong { font-size: 13px; font-weight: 600; display: block; color: #4a5568; }
.upload-zone span  { font-size: 11px; }
.upload-zone:hover { border-color: #2b7de9; background: #f0f8ff; color: #2b7de9; }
.upload-zone:hover strong { color: #2b7de9; }
.file-chip-list { margin-top: 10px; display: flex; flex-direction: column; gap: 6px; }
.file-chip {
  border: 1px solid #bee3f8; border-radius: 9px;
  padding: 8px 12px; background: #f0f8ff;
  display: flex; align-items: center; gap: 9px;
}
.file-chip i { color: #2b7de9; font-size: 15px; flex-shrink: 0; }
.file-chip .fa-file-pdf { color: #c53030; }
.file-name { font-size: 13px; color: #1a56c4; font-weight: 500; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.remove-file { background: none; border: none; color: #a0aec0; cursor: pointer; font-size: 13px; font-family: inherit; flex-shrink: 0; padding: 0 2px; }
.remove-file:hover { color: #c53030; }
.btn-primary:disabled {
  background: #cbd5e0; cursor: not-allowed; opacity: 1;
}
.btn-primary:disabled:hover { background: #cbd5e0; }

/* ── AI hint ── */
.ai-hint { margin-top: 12px; display: flex; align-items: center; gap: 7px; font-size: 11px; color: #a0aec0; }
.ai-hint i { color: #0d9488; }

/* ── Submit error ── */
.submit-error {
  margin-top: 10px; padding: 9px 12px; background: #fff5f5;
  color: #c53030; border-radius: 7px; font-size: 13px;
  display: flex; align-items: center; gap: 8px;
}

/* ── Actions ── */
.form-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px; padding-top: 14px; border-top: 1px solid #f0f2f5; }
.btn-primary  { padding: 10px 22px; background: #2b7de9; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 7px; }
.btn-primary:hover  { background: #1a56c4; }
.btn-secondary { padding: 10px 18px; background: white; color: #4a5568; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 14px; cursor: pointer; }
.btn-secondary:hover { background: #f7fafc; }

/* ── Pipeline ── */
.pipeline-state { padding: 4px 0; }
.pipeline-title { font-size: 14px; font-weight: 700; color: #1a202c; margin-bottom: 14px; display: flex; align-items: center; gap: 8px; }
.pipeline-title i { color: #2b7de9; }
.pipe-step {
  display: flex; align-items: center; gap: 12px;
  padding: 9px 0; border-bottom: 1px solid #f0f2f5;
  opacity: 0; transform: translateX(-6px); transition: opacity 0.25s, transform 0.25s;
}
.pipe-step:last-child { border-bottom: none; }
.pipe-step.show { opacity: 1; transform: translateX(0); }
.pipe-icon { width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0; background: #f0f2f5; color: #a0aec0; display: flex; align-items: center; justify-content: center; font-size: 13px; transition: all 0.3s; }
.pipe-icon.running  { background: #ebf4ff; color: #2b7de9; }
.pipe-icon.done     { background: #e6fffa; color: #0d9488; }
.pipe-icon.done-warn { background: #fffbeb; color: #92400e; }
.pipe-body { flex: 1; }
.pipe-label { font-size: 13px; color: #718096; font-weight: 500; }
.pipe-label.running { color: #2b7de9; font-weight: 600; }
.pipe-label.done    { color: #1a202c; }
.pipe-result { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; border: 1.5px solid; margin-top: 3px; }
.result-idea     { background: #ebf4ff; color: #1a56c4; border-color: #90cdf4; }
.result-gremlin  { background: #fffbeb; color: #92400e; border-color: #fde68a; }
.result-consider { background: #e6fffa; color: #0d9488; border-color: #81e6d9; }
.result-activate { background: #fff0ed; color: #c05621; border-color: #f6ad55; }
.pipe-status { flex-shrink: 0; font-size: 13px; }
.fa-spin.pipe-spin { color: #2b7de9; }
.pipe-check { color: #0d9488; }
.pipe-warn  { color: #92400e; }

/* ── Success ── */
.success-state { text-align: center; padding: 32px 16px; }
.success-icon  { font-size: 52px; color: #0d9488; margin-bottom: 14px; display: block; }
.success-state h3 { font-size: 20px; font-weight: 700; color: #1a202c; margin: 0 0 8px; }
.success-state p  { color: #718096; font-size: 14px; margin: 0 0 24px; }
</style>
